'use client'

import { Button } from '@repo/design-system-cf/components/button'
import { tw } from 'utils/classnames'
import { CheckIcon } from 'primitives/icons'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { useTranslations } from 'next-intl'
import { ActivateTrial } from 'payments/activate-trial-button'
import { UPGRADE_SOURCES } from 'payments/upgrade-sources'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { BannerContainer } from './container'

interface Props {
  banner: Omit<
    NonNullable<SiteDataQuery['siteData']['allAccessYearlyBanner']>,
    '__typename' | 'type'
  > & {
    type: string
  }
}

export function AllAccessYearly({ banner }: Props) {
  const t = useTranslations('cf_banners')
  const { features, headline, buttonText } = banner

  function onButtonClick() {
    const isDailyGiftsPage = window.location.pathname.startsWith(AppRoutes.DAILY_GIFTS)
    savePaymentModalEntryPoint(
      isDailyGiftsPage
        ? PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_DAILY_GIFTS
        : PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV,
    )

    sendAmplitudeEvent(AmplitudeEvents.CLICKED_BANNER_ABOVE_NAV, {
      banner_above_nav: banner.bannerAboveNav,
      banner_above_nav_state: banner.bannerAboveNavState,
    })
  }

  const backgroundImage: string | null = banner.backgroundImage

  return (
    <div
      style={{
        '--checkIconColor': '#3ab561',
      }}
    >
      <BannerContainer
        backgroundImage={backgroundImage}
        wrapperClassName="items-start"
        bannerType={banner.type}
      >
        <div className="w-full">
          <div
            className="mb-[10px] text-center text-[12px] leading-7 tracking-[0.3px] text-[#2A1F3F] md:text-left md:text-[16px] [&_.u-bold]:text-[16px] [&_.u-bold]:font-bold md:[&_.u-bold]:text-[24px]"
            dangerouslySetInnerHTML={{ __html: headline }}
          />

          <ActivateTrial.Root upgradeSource={UPGRADE_SOURCES.MARKETPLACE_ACTIVE_TRIAL_TOP_BANNER}>
            <span className="mb-4 flex w-full md:hidden" onClick={onButtonClick}>
              <Button
                asChild
                variant="primary"
                size="large"
                className="w-full uppercase bg-secondary-500 hover:bg-secondary-700"
              >
                <ActivateTrial.Trigger>
                  <ActivateTrial.Label>{buttonText}</ActivateTrial.Label>
                </ActivateTrial.Trigger>
              </Button>
            </span>
            <ActivateTrial.Dialogs />
          </ActivateTrial.Root>

          {features ? (
            <ul className="mt-3.5 hidden md:block">
              {features.map((feat, index) => {
                return (
                  <li
                    key={index}
                    className={tw(
                      'my-1 flex items-center leading-[1.618]',
                      index > 0 && 'hidden md:flex',
                    )}
                  >
                    <CheckIcon className="mr-1.5 text-[--checkIconColor]" />
                    <span dangerouslySetInnerHTML={{ __html: feat }} />
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
        <div className="mt-[20.5px] hidden w-[41%] flex-col items-center md:flex">
          <ActivateTrial.Root upgradeSource={UPGRADE_SOURCES.MARKETPLACE_ACTIVE_TRIAL_TOP_BANNER}>
            <span className="hidden w-full md:flex" onClick={onButtonClick}>
              <Button
                asChild
                variant="primary"
                size="large"
                className="w-full uppercase bg-secondary-500 hover:bg-secondary-700"
              >
                <ActivateTrial.Trigger>
                  <ActivateTrial.Label>{buttonText}</ActivateTrial.Label>
                </ActivateTrial.Trigger>
              </Button>
            </span>
            <ActivateTrial.Dialogs />
          </ActivateTrial.Root>

          <p className="mt-[9px] text-xs">{t('unlimited_downloads')}</p>
        </div>
      </BannerContainer>
    </div>
  )
}
